import {
  DateRangePicker,
  DateRangePickerProps,
} from '@cloudscape-design/components';

import {
  CalendarIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { RefObject, useEffect, useRef } from 'react';

import './date-range.css';

import { useTranslation } from 'react-i18next';

export type DateRangeProps = Omit<
  DateRangePickerProps,
  'value' | 'relativeOptions' | 'isValidRange' | 'i18nStrings'
> & {
  dataTestId?: string;
  filterView?: boolean;
  value?:
    | { endDate: string; startDate: string; type: 'absolute' }
    | {
        key?: string;
        amount: number;
        unit: 'day' | 'week' | 'month' | 'year';
        type: 'relative';
      }
    | null;
  setDateRange?: (
    value: {
      startDate: string;
      endDate: string;
      isRelative?: boolean;
    } | null,
  ) => void;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  permanent?: boolean;
  showInlineClear?: boolean;
  hideIcon?: boolean;
  wrapperClassName?: string;
  borderless?: boolean;
  customRangeValidation?: {
    maxValue?: number;
    minValue?: number;
    unit: 'day' | 'week' | 'month' | 'year';
    message?: string;
  };
};

const relativeOptions = [
  {
    key: 'previous-1-day',
    amount: 1,
    unit: 'day',
    type: 'relative',
  },
  {
    key: 'previous-7-days',
    amount: 7,
    unit: 'day',
    type: 'relative',
  },
  {
    key: 'previous-30-days',
    amount: 30,
    unit: 'day',
    type: 'relative',
  },
  {
    key: 'previous-90-day',
    amount: 90,
    unit: 'day',
    type: 'relative',
  },
] as const;

export const DateRange = ({
  value,
  setDateRange,
  placeholder,
  label,
  onClose,
  filterView,
  permanent,
  showInlineClear,
  hideIcon,
  customRangeValidation,
  wrapperClassName,
  borderless,
  dataTestId,
  ...otherProps
}: DateRangeProps) => {
  const { t } = useTranslation('designSystem');
  const dateRef = useRef<HTMLDivElement>(null);
  const [dates, setValue] = React.useState<DateRangePickerProps.Value | null>(
    value ? { ...value } : null,
  );
  useEffect(() => {
    setValue(value ? { ...value } : null);
  }, [value]);

  const onChange = ({ detail }: any) => {
    setValue(detail?.value);
    if (!detail.value) {
      return setDateRange?.(null);
    }

    if (detail.value.type === 'absolute') {
      setDateRange?.({
        startDate: detail.value.startDate,
        endDate: detail.value.endDate,
        isRelative: false,
      });
    } else {
      const fromDate = new Date();
      let amount = detail.value.amount;
      switch (detail.value.unit) {
        case 'day':
          amount *= 1;
          break;
        case 'month':
          amount *= 30;
          break;
        case 'week':
          amount *= 7;
          break;
        case 'year':
          amount *= 365;
          break;
      }
      fromDate.setDate(fromDate.getDate() - amount);
      setDateRange?.({
        startDate: fromDate.toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        isRelative: true,
      });
    }
  };
  const elem = (
    <div
      ref={dateRef}
      className={`date-range-picker relative${borderless ? ' borderless' : ''}`}
      data-testid={dataTestId}
    >
      <DateRangePicker
        placeholder={placeholder || t('selectDateRange')}
        {...otherProps}
        isValidRange={(value: DateRangePickerProps.Value | null) => {
          if (value?.type === 'absolute') {
            const [startDateWithoutTime] = value?.startDate.split('T') || [];
            const [endDateWithoutTime] = value?.endDate.split('T') || [];
            if (!startDateWithoutTime || !endDateWithoutTime) {
              return {
                valid: false,
                errorMessage: t('theSelectedDateRangeIsIncompleteSelect'),
              };
            }
          }
          if (value?.type === 'relative') {
            if (!value.amount) {
              return {
                errorMessage: t('common:pleaseSelectAValue'),
                valid: false,
              };
            }
            if (
              customRangeValidation &&
              value.unit === customRangeValidation?.unit &&
              ((customRangeValidation?.maxValue &&
                value.amount > customRangeValidation?.maxValue) ||
                (customRangeValidation?.minValue &&
                  value.amount < customRangeValidation?.minValue))
            ) {
              return {
                errorMessage:
                  customRangeValidation?.message ||
                  t('theSelectedCustomRangeFallsOutsideThe'),
                valid: false,
              };
            }
          }

          return {
            valid: true,
          };
        }}
        onChange={onChange}
        relativeOptions={relativeOptions}
        value={dates}
        i18nStrings={{
          todayAriaLabel: 'Today',
          nextMonthAriaLabel: 'Next month',
          previousMonthAriaLabel: 'Previous month',
          customRelativeRangeDurationLabel: 'Duration',
          customRelativeRangeDurationPlaceholder: 'Enter duration',
          customRelativeRangeOptionLabel: 'Custom range',
          customRelativeRangeOptionDescription:
            'Set a custom range in the past',
          customRelativeRangeUnitLabel: 'Unit of time',
          formatRelativeRange: (e) => {
            const t = 1 === e.amount ? e.unit : `${e.unit}s`;
            return `Last ${e.amount} ${t}`;
          },
          formatUnit: (e, t) => (1 === t ? e : `${e}s`),
          relativeModeTitle: 'Relative range',
          absoluteModeTitle: 'Absolute range',
          relativeRangeSelectionHeading: 'Choose a range',
          startDateLabel: 'Start date',
          endDateLabel: 'End date',
          startTimeLabel: 'Start time',
          endTimeLabel: 'End time',
          clearButtonLabel: 'Clear and dismiss',
          cancelButtonLabel: 'Cancel',
          applyButtonLabel: 'Apply',
        }}
        dateOnly
      />
    </div>
  );

  const clearDate = () => {
    setDateRange?.(null);
  };

  if (!filterView) {
    return elem;
  }
  return (
    <FilterWrapper
      label={label}
      value={dates}
      onClose={onClose}
      permanent={permanent}
      clearDate={clearDate}
      showInlineClear={showInlineClear}
      hideIcon={hideIcon}
      dateRef={dateRef}
      wrapperClassName={wrapperClassName}
    >
      {elem}
    </FilterWrapper>
  );
};

const FilterWrapper = ({
  value,
  children,
  label,
  onClose,
  permanent,
  clearDate,
  showInlineClear,
  hideIcon,
  dateRef,
  wrapperClassName,
}: {
  children?: React.ReactNode;
  value?: DateRangePickerProps.Value | null;
  label?: string;
  onClose?: () => void;
  permanent?: boolean;
  clearDate?: () => void;
  showInlineClear?: boolean;
  hideIcon?: boolean;
  dateRef?: RefObject<HTMLDivElement | null>;
  wrapperClassName?: string;
}) => {
  const { t } = useTranslation('designSystem');
  const triggerDatePickerClick = () => {
    if (dateRef?.current) {
      const dateInput = dateRef.current.querySelector('button');
      if (dateInput) {
        dateInput.click();
      }
    }
  };

  return (
    <div
      className={`date-simple relative inline-flex h-[40px] !cursor-pointer items-center justify-between rounded-md border border-gray-300 bg-white text-sm dark:border-gray-600 dark:bg-gray-900 ${wrapperClassName ?? ''}`}
    >
      <div
        data-testid="date-range"
        className="relative flex h-full w-full items-center"
      >
        <div
          className="absolute inset-0 z-10 cursor-pointer bg-transparent"
          onClick={triggerDatePickerClick}
        />
        <div
          className="relative inline-flex items-center gap-1 px-2 text-sm"
          data-testid="date-range-label-wrapper"
        >
          {!hideIcon && <CalendarIcon className="h-4 w-4" />}
          {value &&
            ((value.type === 'absolute' && value.startDate && value.endDate) ||
              (value.type === 'relative' && value.amount)) && (
              <>
                <span className="font-medium">{label}</span>{' '}
                <span className="text-gray-600 dark:text-gray-400">
                  {value.type === 'relative' ? t('in') : t('between')}
                </span>
              </>
            )}
          {children}
        </div>
      </div>
      {onClose && !permanent && (
        <button
          className="flex items-center self-stretch justify-self-stretch rounded-r-md px-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100"
          onClick={onClose}
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      )}
      {showInlineClear && value && clearDate && (
        <button
          className="flex items-center self-stretch justify-self-stretch rounded-r-md px-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100"
          onClick={clearDate}
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};
